// Sprint A · Silent Welcome — Overlay sopra /menu (Beach Menu PRD § 4 v1.3).
//
// Copre l'intero viewport per ~700ms mentre Menu.jsx è già montato sotto.
// Nessuna CTA, nessun tap gestito, nessuna informazione teatrale.
// Rispetta prefers-reduced-motion (statico ~300ms).
//
// Gerarchia (PRD § 1.3): Affidabilità (menu già interattivo sotto) →
// Velocità (< 700ms) → Chiarezza (una frase, un nome postazione) →
// Eleganza (fade-in/fade-out morbidi via --sb-ease).

import React, { useEffect, useState } from "react";

/**
 * Ritorna la frase di saluto in base all'ora locale del dispositivo.
 * Nessuna chiamata esterna, nessuna informazione meteo o contestuale.
 */
function greetingForNow(date = new Date()) {
  const h = date.getHours() + date.getMinutes() / 60;
  if (h >= 6 && h < 11.5) return "Buongiorno";
  if (h >= 11.5 && h < 14) return "È l'ora dell'aperitivo";
  if (h >= 14 && h < 18) return "Buon pomeriggio";
  if (h >= 18 && h < 23) return "Buona serata";
  return "Buonasera";
}

function _prefersReducedMotion() {
  try { return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true; }
  catch { return false; }
}

/**
 * @param {object} props
 * @param {string} props.stationLabel  Etichetta postazione (es. "Ombrellone 47").
 * @param {() => void} [props.onDone]  Callback quando l'overlay è sparito del tutto.
 */
export default function WelcomeOverlay({ stationLabel, onDone }) {
  const [visible, setVisible] = useState(false);   // opacity 1 quando true
  const [gone, setGone] = useState(false);         // unmount finale
  const reduced = _prefersReducedMotion();

  useEffect(() => {
    if (reduced) {
      setVisible(true);
      const t = setTimeout(() => { setGone(true); onDone && onDone(); }, 300);
      return () => clearTimeout(t);
    }
    // Fade-in appena il DOM ha applicato lo stato iniziale opacity:0
    const raf = requestAnimationFrame(() => setVisible(true));
    // Fade-out dopo 200ms in + 300ms hold = 500ms
    const t1 = setTimeout(() => setVisible(false), 500);
    // Unmount dopo altri 200ms di fade-out
    const t2 = setTimeout(() => { setGone(true); onDone && onDone(); }, 700);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [reduced, onDone]);

  if (gone) return null;

  return (
    <div
      data-testid="welcome-overlay"
      aria-hidden="true"
      // Assorbe silenziosamente eventuali tap (nessun handler → nessuna azione).
      // Il background matcha il body via `bg-sand-100`; il gradient del body
      // resta invisibile perché l'overlay è full-opaque nella fase visible.
      className="fixed inset-0 z-50 bg-sand-100 flex flex-col items-center justify-center px-6 select-none"
      style={{
        opacity: visible ? 1 : 0,
        transition: reduced
          ? "none"
          : "opacity 200ms var(--sb-ease, cubic-bezier(0.2, 0.8, 0.2, 1))",
      }}
    >
      <div className="text-center">
        <div className="font-serif text-5xl text-ink-900" data-testid="welcome-greeting">
          {greetingForNow()}
        </div>
        {stationLabel && (
          <div
            className="mt-3 font-serif italic text-xl text-ink-700"
            data-testid="welcome-station"
          >
            {stationLabel}
          </div>
        )}
      </div>
    </div>
  );
}

// Export named helper for testability
export { greetingForNow };
